import { Perlin } from '../core/noise'
import { mulberry32 } from '../core/rng'

export const ARCHETYPES = ['planinska', 'ravnicarska', 'priobalna', 'isparcelisana'] as const
export type Archetype = (typeof ARCHETYPES)[number]

export const ARCHETYPE_OPIS: Record<Archetype, string> = {
  planinska: 'Kule su skupe, ali jedna dobra vidi daleko.',
  ravnicarska: 'Sve se vidi — blokiranje protivnika je glavna igra.',
  priobalna: 'Koridori uz obalu, bitka oko uskih grla.',
  isparcelisana: 'Mnogo malih prepreka — mreža mora da vijuga.',
}

/** Odakle je teren došao — bojenje i izbor gradova zavise od toga. */
export type VrstaTerena = 'proceduralna' | 'evropa'

export interface HeightmapOpts {
  size: number
  res: number
  seed: number
  vrsta: VrstaTerena
  arhetip?: Archetype
  /** Visine u svetskim jedinicama, mreža (res+1)². Nivo mora je y = 0. */
  heights: Float32Array
}

/**
 * Visinska mapa terena. Nivo mora je uvek y = 0, kopno je iznad.
 * Vrednosti su u svetskim jedinicama, tako da raycast vidljivosti
 * kasnije radi direktno nad ovim podacima bez ikakve konverzije.
 *
 * Klasa ne generiše ništa — samo drži podatke i izvedene veličine.
 * Proceduralni teren pravi `generisiProceduralnu`, Evropu `generisiEvropu`.
 */
export class Heightmap {
  readonly size: number
  readonly res: number
  readonly cell: number
  readonly heights: Float32Array
  readonly seed: number
  readonly vrsta: VrstaTerena
  readonly arhetip?: Archetype

  minY = 0
  maxY = 0
  /** Visina ispod koje je 94% kopna — referenca za bojenje pojaseva. */
  refVisina = 1
  /** Grubi ambijentalni zaklon po temenu: <1 u dolinama, >1 na grebenima. */
  readonly ao: Float32Array
  /** Suvoća kraja po temenu, 0–1. Postavlja je samo Evropa. */
  biom: Float32Array | null = null

  constructor(opts: HeightmapOpts) {
    this.size = opts.size
    this.res = opts.res
    this.cell = opts.size / opts.res
    this.seed = opts.seed
    this.vrsta = opts.vrsta
    this.arhetip = opts.arhetip
    this.heights = opts.heights
    this.ao = new Float32Array(this.heights.length)
    this.preracunaj()
  }

  /**
   * Izvedene veličine iz visina. Zvati ponovo posle svake izmene `heights`
   * — npr. kad putevi poravnaju teren ispod sebe.
   */
  preracunaj(): void {
    this.minY = Infinity
    this.maxY = -Infinity
    for (let k = 0; k < this.heights.length; k++) {
      const y = this.heights[k]
      if (y < this.minY) this.minY = y
      if (y > this.maxY) this.maxY = y
    }
    if (!isFinite(this.minY)) {
      this.minY = 0
      this.maxY = 1
    }
    this.refVisina = this.percentilKopna(0.94)
    this.izracunajAo()
  }

  /**
   * Visina ispod koje je zadati udeo kopna. Bez ovoga bojenje pojaseva ide
   * po apsolutnom maksimumu, pa nekoliko usamljenih vrhova spljošti celu
   * paletu i cela mapa ispadne u boji peska.
   */
  private percentilKopna(udeo: number): number {
    const kante = 256
    const histogram = new Uint32Array(kante)
    let ukupno = 0
    for (let k = 0; k < this.heights.length; k++) {
      const y = this.heights[k]
      if (y <= 0) continue
      const idx = Math.min(kante - 1, Math.floor((y / this.maxY) * kante))
      histogram[idx]++
      ukupno++
    }
    if (ukupno === 0) return Math.max(1, this.maxY)
    const cilj = ukupno * udeo
    let zbir = 0
    for (let i = 0; i < kante; i++) {
      zbir += histogram[i]
      if (zbir >= cilj) return Math.max(1, ((i + 1) / kante) * this.maxY)
    }
    return Math.max(1, this.maxY)
  }

  /**
   * Razlika visine i zamućene visine daje jeftin ambijentalni zaklon.
   * Doline potamne, grebeni se istaknu — reljef postaje čitljiv i kad
   * sunce pada pod uglom pod kojim bi se sve slilo u istu boju.
   */
  private izracunajAo(): void {
    const zamucena = new Float32Array(this.heights)
    const tmp = new Float32Array(this.heights.length)
    const n = this.res + 1
    for (let p = 0; p < 8; p++) {
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
          const l = zamucena[j * n + Math.max(0, i - 1)]
          const r = zamucena[j * n + Math.min(n - 1, i + 1)]
          const g = zamucena[Math.max(0, j - 1) * n + i]
          const d = zamucena[Math.min(n - 1, j + 1) * n + i]
          tmp[j * n + i] = (l + r + g + d) * 0.25
        }
      }
      zamucena.set(tmp)
    }
    const skala = Math.max(6, this.maxY * 0.09)
    for (let k = 0; k < this.heights.length; k++) {
      const razlika = (this.heights[k] - zamucena[k]) / skala
      this.ao[k] = 0.72 + 0.36 * smoothstep(-1, 1, razlika)
    }
  }

  /** Visina u tački mreže, sa klampovanjem na ivice. */
  at(i: number, j: number): number {
    const n = this.res + 1
    const x = i < 0 ? 0 : i >= n ? n - 1 : i
    const y = j < 0 ? 0 : j >= n ? n - 1 : j
    return this.heights[y * n + x]
  }

  /** Bilinearno uzorkovanje u svetskim koordinatama (centar mape je 0,0). */
  sample(x: number, z: number): number {
    const gx = (x + this.size / 2) / this.cell
    const gz = (z + this.size / 2) / this.cell
    const i = Math.floor(gx)
    const j = Math.floor(gz)
    const tx = gx - i
    const tz = gz - j
    const h00 = this.at(i, j)
    const h10 = this.at(i + 1, j)
    const h01 = this.at(i, j + 1)
    const h11 = this.at(i + 1, j + 1)
    return (
      h00 * (1 - tx) * (1 - tz) + h10 * tx * (1 - tz) + h01 * (1 - tx) * tz + h11 * tx * tz
    )
  }

  /** Nagib u tački mreže, 0 = ravno, 1 = vertikalno. */
  slopeAt(i: number, j: number): number {
    const dx = (this.at(i + 1, j) - this.at(i - 1, j)) / (2 * this.cell)
    const dz = (this.at(i, j + 1) - this.at(i, j - 1)) / (2 * this.cell)
    const nagib = Math.sqrt(dx * dx + dz * dz)
    return nagib / (1 + nagib)
  }
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

// ---------------------------------------------------------------------------
// Proceduralni teren
// ---------------------------------------------------------------------------

interface Profil {
  /** Vertikalna skala reljefa u svetskim jedinicama. */
  visina: number
  /** Koliki deo raspona pada ispod nivoa mora. */
  more: number
  /** Osnovna frekvencija šuma. */
  frekvencija: number
  /** Udeo ridged šuma (oštri grebeni) naspram mekog fbm-a. */
  grebeni: number
  oktave: number
  /** Jačina domain warp-a — koliko su obrisi organski. */
  warp: number
}

const PROFILI: Record<Archetype, Profil> = {
  planinska: { visina: 300, more: 0.3, frekvencija: 2.1, grebeni: 0.72, oktave: 7, warp: 0.28 },
  ravnicarska: { visina: 78, more: 0.34, frekvencija: 1.5, grebeni: 0.12, oktave: 5, warp: 0.2 },
  priobalna: { visina: 180, more: 0.46, frekvencija: 2.4, grebeni: 0.5, oktave: 6, warp: 0.34 },
  isparcelisana: { visina: 150, more: 0.32, frekvencija: 4.6, grebeni: 0.42, oktave: 7, warp: 0.16 },
}

export function generisiProceduralnu(opts: {
  arhetip: Archetype
  seed: number
  size: number
  res: number
}): Heightmap {
  const n = opts.res + 1
  const polje = new Float32Array(n * n)

  const p = PROFILI[opts.arhetip]
  const osnovni = new Perlin(opts.seed)
  const warpA = new Perlin(opts.seed ^ 0x9e3779b9)
  const warpB = new Perlin(opts.seed ^ 0x85ebca6b)
  const detalj = new Perlin(opts.seed ^ 0xc2b2ae35)

  // Nasumična orijentacija obale, da priobalne mape ne gledaju uvek isto.
  const rnd = mulberry32(opts.seed ^ 0x27d4eb2f)
  const ugao = rnd() * Math.PI * 2
  const obalaX = Math.cos(ugao)
  const obalaZ = Math.sin(ugao)

  for (let j = 0; j < n; j++) {
    const nz = (j / opts.res) * 2 - 1
    for (let i = 0; i < n; i++) {
      const nx = (i / opts.res) * 2 - 1

      // Domain warp — pomera uzorkovanje da obrisi ne budu očigledno "šumoviti".
      const wx = nx + p.warp * warpA.fbm(nx * 1.7 + 11.3, nz * 1.7 - 4.1, 3)
      const wz = nz + p.warp * warpB.fbm(nx * 1.7 - 7.9, nz * 1.7 + 2.6, 3)

      const fx = wx * p.frekvencija
      const fz = wz * p.frekvencija

      const meko = osnovni.fbm(fx, fz, p.oktave) * 0.5 + 0.5
      const ostro = osnovni.ridged(fx + 100, fz - 100, p.oktave)
      let h = meko * (1 - p.grebeni) + ostro * p.grebeni

      h += detalj.fbm(fx * 6.5, fz * 6.5, 3) * 0.045
      h *= maska(opts.arhetip, nx, nz, obalaX, obalaZ, warpA)

      polje[j * n + i] = h
    }
  }

  // Jedan prolaz uvek — skida sub-piksel nazubljenost na grebenima.
  // Ravničarska mapa dobija još dva, ne sme da bude rovita.
  zagladi(polje, opts.res, opts.arhetip === 'ravnicarska' ? 3 : 1)

  let lo = Infinity
  let hi = -Infinity
  for (let k = 0; k < polje.length; k++) {
    if (polje[k] < lo) lo = polje[k]
    if (polje[k] > hi) hi = polje[k]
  }
  const raspon = Math.max(1e-6, hi - lo)
  for (let k = 0; k < polje.length; k++) {
    const t = (polje[k] - lo) / raspon
    // Kopno se blago diže po krivoj — obale su plitke, vrhovi oštriji.
    const kopno = t > p.more ? Math.pow((t - p.more) / (1 - p.more), 1.45) : 0
    const dubina = t > p.more ? 0 : -Math.pow((p.more - t) / p.more, 0.85)
    polje[k] = kopno * p.visina + dubina * p.visina * 0.22
  }

  return new Heightmap({
    size: opts.size,
    res: opts.res,
    seed: opts.seed,
    vrsta: 'proceduralna',
    arhetip: opts.arhetip,
    heights: polje,
  })
}

/** Maska kopna: gura ivice mape pod vodu, da mapa ima okvir. */
function maska(
  a: Archetype,
  nx: number,
  nz: number,
  obalaX: number,
  obalaZ: number,
  warp: Perlin,
): number {
  // Blago izlomljena ivica, da granica kopna ne bude savršen krug.
  const sum = warp.fbm(nx * 2.4 + 31.7, nz * 2.4 - 19.2, 4) * 0.16

  if (a === 'priobalna') {
    const t = (nx * obalaX + nz * obalaZ) * 0.5 + 0.5 + sum
    const kopno = smoothstep(0.18, 0.62, t)
    const spolja = smoothstep(1.06, 0.82, Math.max(Math.abs(nx), Math.abs(nz)))
    return Math.max(0, kopno * spolja)
  }

  const d = Math.sqrt(nx * nx + nz * nz) + sum
  const okvir = smoothstep(1.02, 0.5, d)
  return 0.18 + 0.82 * okvir
}

function zagladi(polje: Float32Array, res: number, prolaza: number): void {
  const n = res + 1
  const tmp = new Float32Array(polje.length)
  for (let p = 0; p < prolaza; p++) {
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        let s = 0
        let c = 0
        for (let dj = -1; dj <= 1; dj++) {
          for (let di = -1; di <= 1; di++) {
            const x = i + di
            const y = j + dj
            if (x < 0 || y < 0 || x >= n || y >= n) continue
            s += polje[y * n + x]
            c++
          }
        }
        tmp[j * n + i] = s / c
      }
    }
    polje.set(tmp)
  }
}
